import { useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { type FormEvent, useCallback, useMemo } from 'react'
import { useEditAccountName } from '@/src/stores/user/actions'
import { userAtom } from '@/src/stores/user/atoms'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'

export const EditAccountName = () => {
  const { mutate, isPending, isSuccess, isError } = useEditAccountName()
  const user = useAtomValue(userAtom)

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const formData = new FormData(event.currentTarget)

      const accountName = formData.get('accountName') as string | null

      if (accountName) {
        await mutate({
          name: accountName,
        })
      }
    },
    [mutate],
  )

  const buttonText = useMemo(() => {
    if (isPending) {
      return 'Saving...'
    }

    if (isSuccess) {
      return 'Successfully saved'
    }

    if (isError) {
      return 'Something went wrong, try again.'
    }

    return 'Save changes'
  }, [isPending, isSuccess, isError])

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col items-center pb-10'
      onSubmit={onSubmit}
      method='POST'
    >
      <p className='text-secondary text-center mb-8'>
        This name will be use to address you throughout the app.
      </p>

      <Input
        name='accountName'
        className='mb-4'
        dimension='medium'
        defaultValue={user?.name}
        required
        minLength={1}
      />

      <Button
        type='submit'
        className='w-full'
        variant='primary'
        disabled={isPending || isSuccess}
      >
        {buttonText}
      </Button>
    </motion.form>
  )
}
